<template>
  <div
    class="w-full rounded-[8px] relative overflow-hidden flex flex-col lg:flex-row lg:items-center"
    :class="{ 'lg:items-center': props.imageAlignCenter }"
    style="
      border: 1px solid #3d1b60;
      background: linear-gradient(74deg, #2b0152 15.23%, #11002d 76.02%);
    "
    :style="cardStyle"
  >
    <!-- Content -->
    <div class="relative p-6 lg:p-10 lg:pl-12 flex flex-col lg:justify-center w-full lg:w-1/2 z-30">
      <h1
        class="w-full lg:max-w-[472px] text-[24px] sm:text-[28px] lg:text-[48px] font-semibold leading-tight bg-clip-text text-transparent font-avant"
        style="
          background: linear-gradient(92deg, #ba55fe -3.57%, #12dbff 80.72%);
          -webkit-background-clip: text;
        "
      >
        <span v-html="heading"></span>
      </h1>
      <p
        class="w-full mt-2 text-sm sm:text-base lg:text-lg font-normal tracking-[0.36px] lg:max-w-none font-avant"
        :class="[colorMode.value !== 'dark' ? 'text-black' : 'text-white']"
      >
        <span v-html="subcopy"></span>
      </p>
      <!-- Mobile hero image -->
      <NuxtImg
        v-if="imageToShow"
        :key="colorMode.value"
        :src="imageToShow"
        :alt="props.alt ?? props.subcopy ?? 'Hero Image'"
        role="presentation"
        fetchpriority="high"
        loading="eager"
        class="lg:hidden pointer-events-none object-contain block mx-auto mt-4 w-full max-w-full sm:w-auto sm:h-auto z-30"
      />
      <div class="block mt-4 md:mt-11">
        <WrButton
          :link="ctaLink || undefined"
          @click="!ctaLink ? emit('cta-clicked') : undefined"
          container-class="sm:h-[40px] lg:h-[48px]  mx-auto lg:mx-0"
          append-class="text-sm sm:text-md lg:font-normal px-3 lg:px-5 "
        >
          {{ ctaText }}
        </WrButton>
      </div>
    </div>
    <div class="hero-image-wrapper w-full lg:w-1/2 z-30" :class="imagePos ? 'lg:-ml-5' : 'ml-0'">
      <!-- Hero image on right side - Desktop -->
      <NuxtImg
        v-if="imageToShow"
        :key="colorMode.value"
        :src="imageToShow"
        :alt="props.alt ?? props.subcopy ?? 'Hero Image'"
        role="presentation"
        fetchpriority="high"
        aria-hidden="true"
        loading="eager"
        sizes="sm:100vw md:50vw lg:400px"
        class="pointer-events-none mx-auto w-full h-auto object-cover hidden lg:block"
      />
    </div>
    <!-- Decorative horizontal lines SVG -->
    <WrHorizontalLinesSvg v-if="!props.hideLines" />
  </div>
</template>

<script setup lang="ts">
const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

const emit = defineEmits<{
  "cta-clicked": [];
}>();

type Props = {
  heading?: string;
  subcopy?: string;
  subcopyClass?: string;
  subcopyWidth?: string;
  ctaText?: string;
  ctaLink?: string | null;
  imageSrc?: string;
  lightImageSrc?: string;
  hideImage?: boolean;
  variant?: "default" | "large";
  imagePos?: boolean;
  imageHeight?: string;
  topPosition?: string;
  mobileImageMarginTop?: string;
  mobileImageSize?: string;
  mobileImageMarginLeft?: string;
  mobileImageMarginBottom?: string;
  hideLines?: boolean;
  backgroundImage?: string;
  lightBackgroundImage?: string;
  alt?: string;
  height?: string;
  width?: string;
  imageAlignCenter?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  heading: "Transforming Ideas into Apps",
  subcopy:
    "Turning your vision into engaging, seamless, and powerful mobile solutions.",
  subcopyClass: "",
  subcopyWidth: "",
  ctaText: "Ready, Set, App!",
  ctaLink: null,
  imageSrc: "",
  lightImageSrc: "",
  hideImage: false,
  variant: "default",
  imagePosition: "",
  imageHeight: "",
  topPosition: "",
  mobileImageMarginTop: "",
  mobileImageSize: "",
  mobileImageMarginLeft: "",
  mobileImageMarginBottom: "",
  hideLines: false,
  backgroundImage: "",
  lightBackgroundImage: "",
  imagePos: false,
  imageAlignCenter: false,
});

const imageToShow = computed(() => {
  if (props.hideImage) return "";
  if (props.imageSrc) {
    if (isLight.value) {
      return props.imageSrc.replace(/-dark(\.\w+)$/i, "-light$1");
    }
    return props.imageSrc;
  }
  if (isLight.value) {
    return (
      props.lightImageSrc ||
      "/images/Mobile-App-Design-HeroImage_LightMode.webp"
    );
  }
  return "/images/Mobile-App-Design-HeroImage.webp";
});

const cardStyle = computed(() => {
  const baseStyle = isLight.value
    ? {
        borderRadius: "8px",
        border: "1px solid rgba(192, 108, 253, 0.4)",
        background: "#FFF",
      }
    : {};
  if (isLight.value && props.lightBackgroundImage) {
    return {
      ...baseStyle,
      backgroundImage: `url('${props.lightBackgroundImage}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    };
  }
  if (props.backgroundImage) {
    return {
      ...baseStyle,
      backgroundImage: `url('${props.backgroundImage}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    };
  }
  return baseStyle;
});
</script>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>